import { usePlacementEventContextAggregator } from '@eventbrite/event-tracker';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import {
    trackAnalyticsEvent,
    trackEventFromState,
} from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import { isEmpty } from 'lodash';
import React, {
    FocusEventHandler,
    FunctionComponent,
    MouseEventHandler,
    useContext,
    useRef,
} from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useMount } from 'react-use';
import { compose } from 'redux';
import {
    GA_SEARCH_CATEGORY,
    SEARCH_EVENT_NAVIGATION,
    SEARCH_SECTION,
} from '../../../../constants/analytics';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import { setHoverEventTarget as setHoverEventTargetAction } from '../../../../redux/actions/ui';
import { TrackingContext } from '../../../../trackingContext';
import { Event, FeatureFlags, PaginationData } from '../../../../types';
import { useSearchServerEnrolledExperiment } from '../../context/searchExperiment/SearchServerEnrolledExperiment';
import { addSearchIdToGaAction } from '../../utils/analytics';
import { aggregateTrackingContext } from '../../utils/tracking';
import { SearchPrimaryResultsEventCardWrapper } from './SearchPrimaryResultsEventCard';

const LoadingCards = loadable(
    () => import('../../../../components/LoadingCards'),
);

const LIST_TYPE = 'list';

type SearchResultsProps = {
    events: Event[];
    isLoading: boolean;
    pagination: PaginationData;
};

type SearchResultsReduxProps = {
    history?: $FixMe;
    onMouseOverCard?: Function;
    onMouseOutCard?: MouseEventHandler<HTMLDivElement> &
        FocusEventHandler<HTMLDivElement>;
    isAuthenticated?: boolean;
    isOnline?: boolean;
    isMobile?: boolean;
    featureFlags?: FeatureFlags;
    shouldShowDebugOutput?: boolean;
    setHoverEventTarget: Function;
    userId: string;
    searchId: string;
    eventSearch: $FixMe;
    focusedEvents: string[];
    raisedEvents: string[];
    sessionId: string;
    guestId: string;
    // Tracking functions
    trackGAEvent: Function;
    onTrackEventClickInternal: Function;
};

export const SearchResultsEvents = (
    props: SearchResultsProps & SearchResultsReduxProps,
) => {
    const {
        events = [],
        eventSearch,
        searchId,
        userId,
        sessionId,
        guestId,
        isLoading,
        pagination,
    } = props;
    const placementDomElement = useRef(null);
    const { locale } = useContext(TrackingContext);
    const experiment = useSearchServerEnrolledExperiment();
    const hasEventResults = !isEmpty(events);
    const { handleTrackClick, handleTrackClickInternal } =
        useTrackingHandlers(props);
    useTrackDedupeEvents(events);
    usePlacementEventContextAggregator(placementDomElement, ({ id }) =>
        aggregateTrackingContext({
            id,
            events,
            eventSearch,
            searchId,
            userId,
            currentPage: pagination.page,
            locale,
            sessionId,
            guestId,
        }),
    );

    const useGridCards = experiment?.search_event_card_mobile_size === 'grid';

    const useGridLoading =
        useGridCards &&
        HAS_WINDOW &&
        !window.matchMedia('(min-width: 1222px)').matches;

    if (!props.isLoading && !hasEventResults) {
        return null;
    }

    if (isLoading) {
        return (
            <section ref={placementDomElement}>
                <LoadingCards
                    style={useGridLoading ? 'grid' : 'list-responsive'}
                    isFlipped={!useGridLoading}
                />
            </section>
        );
    }

    return (
        <section ref={placementDomElement}>
            <ul className="search-main-content__events-list">
                {events.map(({ id, ...cardProps }: any, index: number) => (
                    <li key={id}>
                        <SearchPrimaryResultsEventCardWrapper
                            trackClick={() => handleTrackClick(index, id)}
                            type={LIST_TYPE}
                            isAuthenticated={props.isAuthenticated}
                            containerSpacingClasses={
                                props.isMobile ? '' : 'eds-l-pad-all-4'
                            }
                            trackClickInternal={() =>
                                handleTrackClickInternal(index, id)
                            }
                            trackGAEvent={props.trackGAEvent}
                            section={SEARCH_SECTION}
                            featureFlags={props.featureFlags}
                            position={index + 1}
                            shouldShowDebugOutput={props.shouldShowDebugOutput}
                            history={props.history}
                            setHoverEventTarget={props.setHoverEventTarget}
                            isLazyImage={index > 3}
                            heapLocationString="Search - Primary Results"
                            isRaised={
                                props.focusedEvents.includes(id) ||
                                props.raisedEvents.includes(id)
                            }
                            scrollId={props.focusedEvents[0]}
                            id={id}
                            {...cardProps}
                        />
                    </li>
                ))}
            </ul>
        </section>
    );
};

function useTrackingHandlers({
    trackGAEvent,
    onTrackEventClickInternal,
}: Pick<
    SearchResultsReduxProps,
    'trackGAEvent' | 'onTrackEventClickInternal'
>) {
    const handleTrackClick = (index: number, eventId: string) => {
        trackGAEvent({
            category: GA_SEARCH_CATEGORY,
            label: eventId,
            dimensions: {
                eventId,
            },
            metrics: {
                searchRank: index,
            },
        });
    };

    const handleTrackClickInternal = (
        index: number,
        id: string,
        ticketsBy?: string,
    ) => {
        const trackingData = {
            clickRank: index,
            navigation: SEARCH_EVENT_NAVIGATION,
            itemId: id,
        };

        onTrackEventClickInternal(trackingData, ticketsBy);
    };

    return {
        handleTrackClick,
        handleTrackClickInternal,
    };
}

const useTrackDedupeEvents = (content: $FixMe) => {
    useMount(() => {
        const checkForDedup = ({
            dedup,
        }: {
            dedup?: { count: number; hash?: string };
        }) => dedup && typeof dedup.count === 'number' && dedup.count > 1;
        const hasDedupedEvent = content?.some((event: $FixMe) =>
            checkForDedup(event),
        );

        if (hasDedupedEvent) {
            trackAnalyticsEvent({
                category: 'search',
                action: 'DeDupedResultsShown',
            });
        }
    });
};

export const _mapStateToProps = (state: any, ownProps: SearchResultsProps) => {
    return {
        eventSearch: state.search.eventSearch,
        isAuthenticated: state.auth.isAuthenticated,
        featureFlags: state.app.featureFlags,
        shouldShowDebugOutput: state.admin.shouldShowDebugOutput,
        trackGAEvent: (action: any) =>
            trackEventFromState(state, addSearchIdToGaAction(action, state)),
        userId: state.app.publicId,
        searchId: state.search.searchId,
        focusedEvents: state.ui.focusedEventCards,
        raisedEvents: state.ui.raisedEventCards,
        sessionId: state.app.sessionId,
        guestId: state.app.guestId,
        ...ownProps,
    };
};

const _mapDispatchToProps = (dispatch: Function) => ({
    setHoverEventTarget: (...args: [$FixMe]) =>
        dispatch(setHoverEventTargetAction(...args)),
    onTrackEventClickInternal: (...args: [$FixMe]) =>
        dispatch(trackEventClickInternalAction(...args)),
});

export const PrimarySearchResultsEventsContainer: FunctionComponent<SearchResultsProps> =
    compose<$FixMe>(
        withRouter,
        connect(_mapStateToProps, _mapDispatchToProps),
    )(SearchResultsEvents);
